import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreditCard, ShieldCheck, ShieldOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Load the Square Web Payments SDK exactly once per page. The URL differs
// between sandbox and production, so we thread it through the caller and
// key the promise off of it. Idempotent — repeat calls resolve immediately.
let _sdkPromise = null;
function loadSquareSdk(environment) {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.Square) return Promise.resolve(window.Square);
  if (_sdkPromise) return _sdkPromise;
  const url = environment === "production"
    ? "https://web.squarecdn.com/v1/square.js"
    : "https://sandbox.web.squarecdn.com/v1/square.js";
  _sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Square));
      existing.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = url;
    s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error("Square SDK failed to load"));
    document.head.appendChild(s);
  });
  return _sdkPromise;
}

// Human-readable "Card ending 1234 · Visa" line.
function CardSummary({ status }) {
  if (!status?.enabled) return null;
  const brand = (status.brand || "card").toString();
  const brandDisplay = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  const exp = status.exp_month && status.exp_year
    ? ` · Expires ${String(status.exp_month).padStart(2, "0")}/${String(status.exp_year).slice(-2)}`
    : "";
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="autopay-card-summary">
      <CreditCard className="h-4 w-4 text-primary" />
      <span className="font-medium">{brandDisplay} •••• {status.last4 || "····"}</span>
      <span className="text-muted-foreground">{exp}</span>
    </div>
  );
}

/**
 * Autopay enrollment panel.
 *
 * Props:
 *   pharmacyId  — the pharmacy whose card is being managed
 *   pharmacyName — display name in the consent copy
 *   onChange    — optional callback fired after enroll/disable so the parent
 *                 can refresh its own state (e.g. invoice status badges).
 *
 * The component fetches its own state (`GET /pharmacies/:id/autopay`) and
 * public Square config (`GET /company/square-config`) at mount, so the
 * parent doesn't need to prop-drill anything.
 */
export default function AutopayPanel({ pharmacyId, pharmacyName, onChange }) {
  const [config, setConfig] = useState(null);          // /company/square-config
  const [status, setStatus] = useState(null);           // /pharmacies/:id/autopay
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [consented, setConsented] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState("");
  const [showCardForm, setShowCardForm] = useState(false);
  const [error, setError] = useState("");
  const cardRef = useRef(null);        // Square Card instance
  const cardMountedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [cfg, st] = await Promise.all([
        api.get("/company/square-config"),
        api.get(`/pharmacies/${pharmacyId}/autopay`),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);
  useEffect(() => { if (pharmacyId) refresh(); }, [pharmacyId, refresh]);

  // Attach the Square Card element as soon as the "Add card" form is shown.
  // We tear down on unmount so switching tabs / re-mounting doesn't leak
  // two card iframes into the DOM.
  useEffect(() => {
    if (!showCardForm || !config?.enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const Square = await loadSquareSdk(config.environment);
        if (cancelled) return;
        const payments = Square.payments(config.application_id, config.location_id);
        const card = await payments.card();
        await card.attach("#autopay-card-container");
        cardRef.current = card;
        cardMountedRef.current = true;
      } catch (e) {
        setError(e?.message || "Card form failed to load");
      }
    })();
    return () => {
      cancelled = true;
      try { cardRef.current?.destroy?.(); } catch {}
      cardRef.current = null;
      cardMountedRef.current = false;
    };
  }, [showCardForm, config]);

  const enroll = async () => {
    setError("");
    if (!consented) {
      setError("Please check the consent box to continue.");
      return;
    }
    if (!signerName.trim()) {
      setError("Please enter the name of the person authorizing autopay for the pharmacy.");
      return;
    }
    if (!cardRef.current) {
      setError("Card form is not ready — please wait a moment and retry.");
      return;
    }
    setEnrolling(true);
    try {
      const result = await cardRef.current.tokenize({
        intent: "STORE",
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: { givenName: signerName.trim(), countryCode: "US" },
        // NOTE: `currencyCode` must NOT be set for intent="STORE" — Square
        // 2026+ SDK rejects it with "The STORE intent does not support the
        // currencyCode field." It's a charge-time concept; we're only
        // saving the card. (2026-10-05 fix)
      });
      if (result.status !== "OK") {
        const first = (result.errors && result.errors[0]) || {};
        throw new Error(first.message || first.detail || "Card could not be tokenized");
      }
      await api.post(`/pharmacies/${pharmacyId}/autopay/enroll`, {
        source_id: result.token,
        consented: true,
        signer_name: signerName.trim(),
        signer_role_at_pharmacy: signerRole.trim() || null,
        terms_version: config.terms_version,
      });
      toast.success("Autopay enabled. A copy of the consent was emailed to SRCI.");
      setShowCardForm(false);
      setConsented(false);
      setSignerName("");
      setSignerRole("");
      await refresh();
      onChange?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Enrollment failed");
    } finally {
      setEnrolling(false);
    }
  };

  const disable = async () => {
    if (!window.confirm(
      "Disable autopay?\n\nFuture weekly invoices will need to be paid manually via the Square link inside each PDF. A copy of this change will be emailed to SRCI for the audit log."
    )) return;
    try {
      await api.post(`/pharmacies/${pharmacyId}/autopay/disable`);
      toast.success("Autopay disabled.");
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <Card className="p-4" data-testid="autopay-panel-loading">
        <div className="text-sm text-muted-foreground">Loading autopay…</div>
      </Card>
    );
  }

  if (!config?.enabled) {
    return (
      <Card className="p-4" data-testid="autopay-panel-disabled">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldOff className="h-4 w-4" /> Autopay is not configured on this server.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5" data-testid="autopay-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium">Autopay</h3>
            {status?.enabled ? (
              <Badge variant="outline" className="border-green-600/40 text-green-700 bg-green-50" data-testid="autopay-status-enabled">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Enabled
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground" data-testid="autopay-status-disabled">
                Off
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Auto-charge the weekly SRCI invoice to a saved card (base + {((config.surcharge_rate || 0) * 100).toFixed(0)}% surcharge).
          </p>
        </div>
      </div>

      {status?.enabled ? (
        <div className="space-y-3">
          <CardSummary status={status} />
          {status.last_charge_at && (
            <div className="text-xs text-muted-foreground">
              Last charge: {new Date(status.last_charge_at).toLocaleString()} · {status.last_charge_status || "—"}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" onClick={() => setShowCardForm(true)} data-testid="autopay-update-card-btn">
              Update card
            </Button>
            <Button variant="destructive" onClick={disable} data-testid="autopay-disable-btn">
              <ShieldOff className="h-4 w-4 mr-1.5" /> Disable autopay
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {!showCardForm && (
            <Button onClick={() => setShowCardForm(true)} className="rounded-full" data-testid="autopay-enroll-btn">
              <ShieldCheck className="h-4 w-4 mr-1.5" /> Enable autopay
            </Button>
          )}
        </div>
      )}

      {showCardForm && (
        <div className="mt-5 space-y-4 border-t pt-4" data-testid="autopay-card-form">
          <div className="text-xs text-muted-foreground whitespace-pre-line bg-secondary/50 p-3 rounded-md">
            {config.terms_text}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="autopay-signer-name">Authorizing person at {pharmacyName || "the pharmacy"}</Label>
              <Input
                id="autopay-signer-name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Full name"
                data-testid="autopay-signer-name"
              />
            </div>
            <div>
              <Label htmlFor="autopay-signer-role">Role (optional)</Label>
              <Input
                id="autopay-signer-role"
                value={signerRole}
                onChange={(e) => setSignerRole(e.target.value)}
                placeholder="Pharmacist in Charge, Owner, etc."
                data-testid="autopay-signer-role"
              />
            </div>
          </div>

          <div>
            <Label>Card details</Label>
            <div
              id="autopay-card-container"
              className="rounded-md border border-input p-3 bg-background min-h-[68px]"
              data-testid="autopay-square-card-container"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Card details are entered directly into a Square-hosted secure field. SRCI never sees your raw card number.
            </p>
          </div>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="autopay-consent-row">
            <Checkbox
              checked={consented}
              onCheckedChange={(v) => setConsented(!!v)}
              data-testid="autopay-consent-checkbox"
            />
            <span className="text-sm">
              I authorize SRCI Pharmacy Deliveries to charge this card automatically for the weekly delivery invoice as described above. A copy of this authorization will be emailed to SRCI for the audit record.
            </span>
          </label>

          {error && (
            <Alert variant="destructive" data-testid="autopay-error">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={enroll}
              disabled={enrolling || !consented || !signerName.trim()}
              className="rounded-full"
              data-testid="autopay-submit-btn"
            >
              {enrolling ? "Saving…" : "Save & enable autopay"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setShowCardForm(false); setError(""); setConsented(false); }}
              data-testid="autopay-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
